import * as Sentry from "@sentry/react";
import type { ErrorEvent } from "@sentry/react";

// Thin, opt-in Sentry shim for the ace web app — mirrors the backend's sentry
// package (services/ace/backend/.../sentry.go) and the local initPostHog shape.
//
// init is a no-op when no DSN is configured, so local dev and any unconfigured
// build run exactly as before (no SDK, no network). The DSN is injected at build
// time via VITE_SENTRY_DSN (SSM → deploy workflow → Vite); it is publishable, so
// it is never hardcoded here — an absent var just means "errors only locally".
//
// PII minimization (D-04 evaluation firewall, D-05 disclosure): sendDefaultPii
// is false so the SDK never attaches user IPs, cookies, or request data. Tracing
// and Session Replay are deliberately NOT enabled — this is errors-only.

const SENTRY_DSN = import.meta.env.VITE_SENTRY_DSN as string | undefined;
const SENTRY_ENVIRONMENT = import.meta.env.VITE_SENTRY_ENVIRONMENT as string | undefined;

// isThirdPartyStackOverflow reports whether an event is a "Maximum call stack size
// exceeded" whose stack has NO frame from our own bundle — i.e. pure third-party /
// injected noise (ACE-FRONTEND-8). PostHog and iOS in-app browsers (reached via ad
// links like t.co) recurse and surface via window.onerror with an unattributable
// cross-origin / "undefined" frame. Our OWN recursion keeps an orbitlens.io frame
// and returns false, so a genuine bug of ours is never hidden — that safety
// property is pinned by sentry.test.ts.
export function isThirdPartyStackOverflow(event: ErrorEvent): boolean {
  const values = event.exception?.values ?? [];
  const isStackOverflow = values.some((v) =>
    v.value?.includes("Maximum call stack size exceeded"),
  );
  if (!isStackOverflow) return false;
  const frames = values.flatMap((v) => v.stacktrace?.frames ?? []);
  return !frames.some((f) => (f.filename ?? "").includes("orbitlens.io"));
}

let initialized = false;

export function initSentry() {
  if (initialized || !SENTRY_DSN) return;
  Sentry.init({
    dsn: SENTRY_DSN,
    environment: SENTRY_ENVIRONMENT,
    // Errors only: ace observation is not a tracing/replay target.
    tracesSampleRate: 0,
    // PII minimization: never attach IPs, cookies, or request bodies.
    sendDefaultPii: false,
    // Known-benign browser noise only. Real failures (Failed to fetch, chunk
    // load errors) are deliberately NOT listed — they signal deploy drift /
    // outages worth seeing.
    ignoreErrors: [
      // Browser layout-engine self-throttle; no user impact, no defect.
      "ResizeObserver loop limit exceeded",
      "ResizeObserver loop completed with undelivered notifications",
      // A promise rejected with a non-Error value the SDK can't attribute to us.
      "Non-Error promise rejection captured",
    ],
    // Errors thrown from injected browser-extension code, not our bundle.
    denyUrls: [/^chrome-extension:\/\//, /^moz-extension:\/\//, /^safari-extension:\/\//],
    // Drop pure third-party "Maximum call stack size exceeded" noise (see
    // isThirdPartyStackOverflow / ACE-FRONTEND-8) — never our own errors.
    beforeSend: (event: ErrorEvent): ErrorEvent | null =>
      isThirdPartyStackOverflow(event) ? null : event,
  });
  initialized = true;
}

// captureException forwards a render error caught by the ErrorBoundary. Safe to
// call when init was a no-op: the SDK's default client just drops the event.
export function captureException(error: unknown) {
  Sentry.captureException(error);
}
